<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref } from 'vue'
import { useEcommerce, useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { T } from '@tolgee/vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThePremium from '@/components/ThePremium.vue'
import { useConfetti } from '@/composables/game/confetti'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import ShopBadge from '@/components/ShopBadge.vue'
import packValueImage from '@/assets/images/shop-badge-1000.png'
import packValueNameImage from '@/assets/images/badge-value.png'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import LimitedPack from '@/components/LimitedPack.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
  show: boolean
  limitedPack: EcommerceItem
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const { buyingItemId } = useEcommerce()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
} = useEcommerceItemDetailsPurchase(props.limitedPack)
const { goldenCoinValue } = usePlayer()

const purchaseDone = ref(false)
const coinBenefit = computed(() => props.limitedPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN))
const goldenCoinBenefitAmount = computed(() => coinBenefit.value ? (coinBenefit.value?.amount * goldenCoinValue.value) : 0)
const spinBenefit = computed(() => props.limitedPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN))
const tphx2Benefit = computed(() => props.limitedPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2))
const offlineProfitBenefit = computed(() => props.limitedPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT))

const isPurchasing = ref(false)
const isLoading = computed(() => buyingItemId.value !== null || isPurchasing.value)
const starPurchaseLoading = computed(() => isLoading.value && isBuyingWithStars.value)
const tonPurchaseLoading = computed(() => isLoading.value && isBuyingWithTon.value)

async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isPurchasing.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      await showConfetti()
      purchaseDone.value = true
      await syncPlayer()
      await updateFortuneWheelState()
    }
  } finally {
    isPurchasing.value = false
  }
}

function hideModal() {
  if (isPurchasing.value) {
    return
  }
  emit('hide')
  purchaseDone.value = false
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="max-[375px]:pb-6"
    @hide="hideModal"
  >
    <div
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <LimitedPack
        :limited-pack="limitedPack"
        is-large
      >
        <ShopBadge
          :value-image="packValueImage"
          :name-image="packValueNameImage"
          outer-class="-left-2 -top-4 w-[56px] h-auto"
          valueable-image-class="top-1.5 w-[46px]"
          value-image-class="top-[20px] w-[50px]"
          animate-value-image
        />
      </LimitedPack>
      <div class="mt-3 flex flex-col items-center">
        <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          <span v-if="purchaseDone">
            <T
              key-name="starter_pack.title_done"
              default-value="Purchase successful!"
            />
          </span>
          <template v-else>
            {{ limitedPack.name }}
            = ${{ starPrice.usdAmount }}
          </template>
        </div>
        <div class="text-center text-white/60 text-base font-medium">
          <span v-if="purchaseDone">
            {{ limitedPack.name }}
          </span>
          <span v-else>{{ limitedPack.description }}</span>
        </div>
      </div>
      <div class="mt-6 flex flex-col items-center gap-y-3 w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <CoinIcon class="w-5 h-auto min-w-[20px]" />
          <NumberFormatTooltip
            :number="goldenCoinBenefitAmount"
            :formatting-type="'semi'"
          >
            <span
              class="benefit-box-value"
            >{{
              formatNumberSemiTruncate(goldenCoinBenefitAmount)
            }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && spinBenefit"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel-without-border.png"
            class="size-8 min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit.amount}"
            />
          </span>
        </div>
        <PlusIcon
          v-if="(coinBenefit || spinBenefit) && tphx2Benefit"
          class="text-white size-4"
        />
        <div
          v-if="tphx2Benefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/tphx2.png"
            class="size-8 min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              v-if="tphx2Benefit.amount < 1"
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# minute} other {# minutes}}"
              :params="{count: tphx2Benefit.amount * 60}"
            />
            <T
              v-else
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# hour} other {# hours}}"
              :params="{count: tphx2Benefit.amount}"
            />
          </span>
        </div>
        <PlusIcon
          v-if="(coinBenefit || spinBenefit || tphx2Benefit) && offlineProfitBenefit"
          class="text-white size-4"
        />
        <div
          v-if="offlineProfitBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/offline-profit.png"
            class="w-[36px] h-auto object-cover"
          >
          <span class="benefit-box-value">
            <T
              v-if="offlineProfitBenefit.amount < 1"
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {+# minute} other {+# minutes}}"
              :params="{count: offlineProfitBenefit.amount * 60}"
            />
            <T
              v-else
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {+# hour} other {+# hours}}"
              :params="{count: offlineProfitBenefit.amount}"
            />
          </span>
        </div>
      </div>
      <BaseButton
        v-if="purchaseDone"
        class="mt-12 w-full"
        @click="$emit('hide')"
      >
        Close
      </BaseButton>
      <div
        v-else
        class="flex flex-col w-full mt-6"
      >
        <BaseButton
          class="w-full"
          :loading="starPurchaseLoading"
          @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <span
            v-if="!starPurchaseLoading"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <ThePremium :with-tooltip="false" />
            <span class="-ml-0.5">{{ starPrice.amount }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>
        <div class="relative w-full">
          <div class="absolute right-12 top-1 rotate-[20deg]">
            <TonDiscountBadge label="-10%" />
          </div>
          <BaseButton
            v-if="showTonPurchase"
            style-type="tertiary"
            :loading="tonPurchaseLoading"
            class="mt-3 w-full"
            @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
          >
            <span
              v-if="!tonPurchaseLoading"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover"
              >
              <span>{{ tonPrice?.amount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";
</style>
